import React from 'react';

import {
  getMenuItemsFromSections,
  formatMenuWithModifiers
} from 'catering/helpers/menu';
import { useRedirectToLocationOffline } from 'catering/hooks/navigation';
import {
  useMenu as useMenuQuery,
  selectShouldLazyLoadMenu,
  selectMenuHeroImage,
  useMenuDetails
} from 'catering/hooks/query';
import PageLoadSpinner from 'components/PageLoadSpinner';
import { useFilteredMenu } from 'hooks/useFilteredMenu';
import { getMenuItemsModifierAndOptionNames } from 'bento-ordering/menus/utils/getMenuItemsModifierAndOptionNames';

export const MENU_NAV_BAR = 'menu_nav_bar';
export const FILTERED_ITEM_NAV_BAR = 'filtered_item_nav_bar';

export const CateringMenuContext = React.createContext();
CateringMenuContext.displayName = 'CateringMenuContext';

function CateringMenuProvider(props) {
  const {
    data: menuItemDetails,
    isLoading: isLoadingMenuItemDetails,
    isNotMenuAvailable
  } = useMenuDetails();

  useRedirectToLocationOffline([
    {
      shouldRedirect: isNotMenuAvailable,
      logMessage: locationSlug =>
        `Attempted to retrieve firstAvailableFulfillmentHours but none are available - ${locationSlug}`
    }
  ]);

  const menuItemsModifierAndOptionNames =
    !isLoadingMenuItemDetails && menuItemDetails
      ? getMenuItemsModifierAndOptionNames(menuItemDetails)
      : {};

  const { isLoading: isMenuLoading, data: menu } = useMenuQuery({
    select: menu => {
      const shouldLazyLoad = selectShouldLazyLoadMenu(menu);
      const heroImage = selectMenuHeroImage(menu);
      const formattedMenu = formatMenuWithModifiers(
        menu,
        menuItemsModifierAndOptionNames
      );
      return {
        ...formattedMenu,
        shouldLazyLoad,
        heroImage
      };
    }
  });

  const menuItems = getMenuItemsFromSections(menu);
  const numberOfMenuItems = menuItems.length;

  const { setSearchTerm, setFilteredMenu, state } = useFilteredMenu(menu);

  if (isMenuLoading) {
    return <PageLoadSpinner />;
  }

  return (
    <CateringMenuContext.Provider
      value={{
        menu: state.filteredMenu,
        isMenuLoading,
        setFilteredMenu,
        isFilteringInProgress: state.isFilteringInProgress,
        setSearchTerm,
        searchTerm: state.searchTerm,
        numberOfMenuItems
      }}
      {...props}
    />
  );
}

function useMenu() {
  const context = React.useContext(CateringMenuContext);
  if (context === undefined) {
    throw new Error('useMenu must be used within a CateringMenuProvider');
  }
  return context;
}

export { CateringMenuProvider, useMenu };
